import React from "react";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { useLanguage } from "../../contexts/LanguageContext";

export const statusToneClasses = {
  neutral: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  success: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  warning: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  danger: "bg-red-500/20 text-red-400 border-red-500/30",
  accent: "bg-[#E2B05E]/20 text-[#E2B05E] border-[#E2B05E]/30",
  purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  cyan: "bg-[#3AB8D6]/20 text-[#3AB8D6] border-[#3AB8D6]/30",
};

export const statusToneByValue = {
  // Generic
  active: "success",
  inactive: "neutral",
  activo: "success",
  inactivo: "neutral",
  suspendido: "danger",
  archivado: "neutral",

  // Documents
  borrador: "neutral",
  enviada: "info",
  emitida: "info",
  aceptada: "success",
  aprobado: "info",
  aprobada: "success",
  pagada: "success",
  pagado: "success",
  rechazada: "danger",
  rechazado: "danger",
  vencida: "danger",
  cancelada: "neutral",
  cancelado: "danger",
  facturada: "purple",

  // Projects/tasks
  planeacion: "info",
  pendiente: "warning",
  disponible: "info",
  tomada: "purple",
  en_progreso: "cyan",
  entregada: "warning",
  en_revision: "warning",
  requiere_mas_info: "purple",
  aprobada_para_cotizar: "success",
  convertida_a_cotizacion: "cyan",
  en_pausa: "warning",
  pausada: "warning",
  cerrado: "neutral",
  completado: "success",
  completada: "success",
  pausado: "warning",
  resolved: "success",
  closed: "neutral",
  in_progress: "purple",
  on_hold: "warning",
  por_hacer: "neutral",

  // Priority/levels
  baja: "info",
  media: "warning",
  alta: "accent",
  critica: "danger",
  experta: "purple",
  junior: "info",
  semi_senior: "purple",
  senior: "accent",

  // Invoice validation
  pending_validation: "warning",
  validated: "success",
  invalidated: "danger",
};

export function getStatusTone(value, fallback = "neutral") {
  return statusToneByValue[String(value || "").toLowerCase()] || fallback;
}

export function getStatusClassName(value, fallback = "neutral") {
  return statusToneClasses[getStatusTone(value, fallback)] || statusToneClasses.neutral;
}

export const statusTooltipByContext = {
  es: {
    "quotation.borrador": "Cotización en preparación. Todavía no se ha enviado ni presentado al cliente.",
    "quotation.enviada": "Cotización enviada o presentada al cliente. Está pendiente de aceptación o rechazo.",
    "quotation.aceptada": "El cliente aceptó la cotización. Está lista para convertirse en factura.",
    "quotation.rechazada": "El cliente rechazó la cotización o se descartó la propuesta.",
    "quotation.vencida": "La vigencia de la cotización expiró sin confirmación del cliente.",
    "quotation.facturada": "La cotización fue aceptada por el cliente y ya se generó una factura relacionada.",

    "invoice.borrador": "Factura en preparación. Todavía no se ha emitido para cobro.",
    "invoice.emitida": "Factura emitida para gestionar el pago del cliente.",
    "invoice.pagada": "Factura pagada. El cheque o pago ya fue depositado en la cuenta.",
    "invoice.vencida": "Factura emitida cuya fecha de vencimiento ya pasó sin registrarse el pago.",
    "invoice.cancelada": "Factura anulada. No debe contarse como cobro pendiente ni recibido.",

    "purchase_order.pendiente": "Orden de compra pendiente de pago o cierre con el proveedor.",
    "purchase_order.cancelada": "Orden de compra cerrada como pagada/cancelada con referencia bancaria registrada.",
  },
  en: {
    "quotation.borrador": "Quotation in preparation. It has not been sent or presented to the client yet.",
    "quotation.enviada": "Quotation sent or presented to the client. It is waiting for acceptance or rejection.",
    "quotation.aceptada": "The client accepted the quotation. It is ready to be converted into an invoice.",
    "quotation.rechazada": "The client rejected the quotation, or the proposal was discarded.",
    "quotation.vencida": "The quotation validity period expired without client confirmation.",
    "quotation.facturada": "The quotation was accepted by the client and a related invoice was generated.",

    "invoice.borrador": "Invoice in preparation. It has not been issued for collection yet.",
    "invoice.emitida": "Invoice issued to start the client payment process.",
    "invoice.pagada": "Invoice paid. The check or payment has already been deposited into the account.",
    "invoice.vencida": "Issued invoice whose due date has passed without a registered payment.",
    "invoice.cancelada": "Canceled invoice. It should not count as pending or received revenue.",

    "purchase_order.pendiente": "Purchase order pending payment or closure with the supplier.",
    "purchase_order.cancelada": "Purchase order closed as paid/canceled with a registered bank reference.",
  },
};

export function getStatusTooltip(value, context, language = "es") {
  const normalizedValue = String(value || "").toLowerCase();
  const normalizedContext = String(context || "").toLowerCase();
  const normalizedLanguage = language === "en" ? "en" : "es";

  if (!normalizedContext) return "";

  return (
    statusTooltipByContext[normalizedLanguage]?.[
      `${normalizedContext}.${normalizedValue}`
    ] || ""
  );
}

export function StatusBadge({
  value,
  status,
  label,
  tone,
  className,
  context,
  statusContext,
  tooltip,
  children,
  ...props
}) {
  const { isSpanish } = useLanguage();
  const resolvedValue = value ?? status;
  const resolvedTone = tone || getStatusTone(resolvedValue);
  const resolvedTooltip =
    tooltip === false
      ? ""
      : tooltip ||
        getStatusTooltip(
          resolvedValue,
          statusContext || context,
          isSpanish ? "es" : "en"
        );
  const content = children || label || resolvedValue;

  const badge = (
    <Badge
      className={cn(
        statusToneClasses[resolvedTone] || statusToneClasses.neutral,
        resolvedTooltip && "cursor-help",
        className
      )}
      {...props}
    >
      {content}
    </Badge>
  );

  if (!resolvedTooltip) {
    return badge;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex"
            tabIndex={0}
            title={resolvedTooltip}
            aria-label={`${content}: ${resolvedTooltip}`}
          >
            {badge}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs border border-[#242E42] bg-[#0B0E14] text-[#F3F4F6] shadow-xl"
        >
          {resolvedTooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default StatusBadge;
